import { PiX } from 'react-icons/pi';
import Button from '../../atoms/buttons/button';
import { Text } from '../../atoms/text';
import { ErrorState } from '../../atoms/states/error-state';
import { useI18n } from '../../../hooks/provider-bounded/i18n';
import { useEmojiCatalogModule } from '../../../hooks/use-emoji-catalog-module';
import type { EmojiPickerProps } from '../../../types/emoji-picker.types';

/**
 * Shared, lazy native emoji picker with recoverable loading and compact chrome.
 * @param props - Selection, tone and desktop dismissal.
 * @returns The accessible picker contents for an owning popover or dock.
 */
export const EmojiPicker = ({
  onClose,
  compact = false,
  searchMode = false,
  ...catalogProps
}: EmojiPickerProps) => {
  const { t } = useI18n();
  const { Catalog, failed, retry } = useEmojiCatalogModule();
  const closeAction = (
    <Button
      type="button"
      appearance="ghost"
      size="md"
      shape="round"
      isIconOnly
      onClick={onClose}
      aria-label={t('emojiPicker.close')}
    >
      <PiX className="h-4 w-4" aria-hidden />
    </Button>
  );
  return (
    <div
      data-emoji-search={searchMode || undefined}
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white text-neutral-900 [container-type:size]"
    >
      {!compact && (
        <div className="flex shrink-0 items-center justify-between px-3 py-1">
          <Text size="small" weight="medium">
            {t('emojiPicker.title')}
          </Text>
          {closeAction}
        </div>
      )}
      {Catalog ? (
        <Catalog {...catalogProps} compact={compact} searchMode={searchMode} />
      ) : failed ? (
        <div className="min-h-0 overflow-auto" role="alert">
          <ErrorState
            size="xs"
            title={t('emojiPicker.loadError')}
            description=""
            onAction={retry}
            actionContent={t('emojiPicker.retry')}
          />
        </div>
      ) : (
        <div className="p-4" role="status">
          <Text size="small">{t('emojiPicker.loading')}</Text>
        </div>
      )}
    </div>
  );
};
